/**
 * marketingChrome — PatrolPuffin's serializable marketing-website chrome
 * descriptor (BLOG-STD-001, full-shell SSR).
 *
 * `buildMarketingChromeDescriptor({ t })` returns a pure, JSON-able description
 * of the header + footer chrome (nav links, footer columns, locale switcher,
 * social links). It is the SINGLE SOURCE consumed by:
 *   - `createMarketingApp` (client), handed this BUILDER by `MarketingWebsiteApp`, which runs
 *     `buildShellProps` on every render in the language in force (Q-LC24-7).
 *   - the build-time descriptor-gen Vite plugin → POSTed to marketing-cms → the
 *     per-app on-demand prerender harness renders the identical shell server-side.
 *
 * Because BOTH the client and the server resolve `buildShellProps(descriptor,
 * locale)` from the SAME descriptor, the prerendered HTML hydrates with zero
 * chrome mismatch. The descriptor carries NO React nodes + NO class references
 * (e.g. StorageService) — those are injected by `buildShellProps` at call time.
 *
 * @module portals/marketing-website/marketingChrome
 */

// The node-safe subpath, not the barrel: `vite.config.js` imports this descriptor at CONFIG time, where the barrel's
// `.jsx` components cannot load.
import { signupUrlFor } from '@puffin-core/marketing-shell/signup-url';
import { STORAGE_TYPE } from '@puffin-core/storage';
import { LOCALE_STORAGE_KEY } from './i18n/constants';
import { PATROL_MARKETING_ROUTES } from './constants/patrolMarketingRoutePaths';

function buildHeaderNavLinks(t) {
  return [
    { to: PATROL_MARKETING_ROUTES.FEATURES, label: t('nav.features') },
    { to: PATROL_MARKETING_ROUTES.PRICING, label: t('nav.pricing') },
    { to: PATROL_MARKETING_ROUTES.BLOG, label: t('nav.blog', { defaultValue: 'Blog' }) },
    { to: PATROL_MARKETING_ROUTES.CONTACT, label: t('nav.contact') },
  ];
}

function buildFooterColumns(t) {
  return [
    {
      key: 'product',
      heading: t('footer.product'),
      links: [
        { key: 'features', label: t('footer.features'), to: PATROL_MARKETING_ROUTES.FEATURES },
        { key: 'pricing', label: t('footer.pricing'), to: PATROL_MARKETING_ROUTES.PRICING },
        { key: 'contact', label: t('footer.contact'), to: PATROL_MARKETING_ROUTES.CONTACT },
      ],
    },
    {
      key: 'legal',
      heading: t('footer.legal'),
      links: [
        { key: 'privacy', label: t('footer.privacy'), to: PATROL_MARKETING_ROUTES.PRIVACY },
        { key: 'terms', label: t('footer.terms'), to: PATROL_MARKETING_ROUTES.TERMS },
      ],
    },
  ];
}

function buildSocialLinks(t) {
  return [
    {
      key: 'linkedin',
      label: t('footer.social.followOn', { platform: 'LinkedIn' }),
      href: 'https://linkedin.com/company/patrolpuffin',
    },
    {
      key: 'x',
      label: t('footer.social.followOn', { platform: 'X' }),
      href: 'https://x.com/patrolpuffin',
    },
  ];
}

/**
 * Build the serializable chrome descriptor for the active locale's `t`.
 *
 * @param {{ t: (key: string, opts?: object) => string }} args
 * @returns {import('@puffin-core/marketing-shell').ChromeDescriptor}
 */
export default function buildMarketingChromeDescriptor({ t }) {
  return {
    appKey: 'patrol',
    header: {
      navLinks: buildHeaderNavLinks(t),
      localeSwitcher: {
        languages: [
          // PRESERVED — the 'en' label uses the pre-existing
          // `t('common.learnMore', { defaultValue: 'English' })` call.
          { code: 'en', label: t('common.learnMore', { defaultValue: 'English' }) },
          { code: 'es', label: 'Español' },
        ],
        label: 'Language',
        storageKey: LOCALE_STORAGE_KEY,
        storageTypeLocal: STORAGE_TYPE.LOCAL,
      },
      // APP-ENTITLEMENT-001 Phase 11: "Get Started" opens the wizard with this app named, never a contact form.
      signInCta: { label: t('nav.getStarted'), href: signupUrlFor({ app: 'patrol' }) },
      // MKT-LOGIN-XDOMAIN-001: the login CTA now targets the in-site cross-domain
      // login route (was a hardcoded tenant-subdomain URL).
      secondaryCta: { label: t('nav.login'), href: PATROL_MARKETING_ROUTES.LOGIN },
    },
    footer: {
      tagline: t('footer.tagline'),
      columns: buildFooterColumns(t),
      socialLinks: buildSocialLinks(t),
      privacyLabel: t('footer.privacy'),
      termsLabel: t('footer.terms'),
    },
  };
}
